import type { ReactElement } from "react";
import { Link } from "react-router-dom";

import type { CatalogEntry } from "../api/types";
import { useCompare } from "../contexts/CompareContext";
import { SuiteBadge } from "./SuiteBadge";

interface Props {
  entry: CatalogEntry;
}

export function ProductCard({ entry }: Props): ReactElement {
  const { isSelected, toggleProduct, isFull } = useCompare();
  const inCompare = isSelected(entry.slug);

  return (
    <article className="product-card" data-product-card data-slug={entry.slug}>
      <div className="product-card__image" aria-hidden>
        <img
          src={entry.screenshot_url}
          alt={`${entry.name} preview`}
          className="product-card__screenshot"
          loading="lazy"
        />
        <SuiteBadge suite={entry.suite} className="product-card__suite-badge" />
        {/* Cycle 9 STORY-802: Compare toggle */}
        <button
          type="button"
          className={`product-card__compare-toggle${inCompare ? " product-card__compare-toggle--active" : ""}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            toggleProduct(entry.slug);
          }}
          disabled={!inCompare && isFull}
          title={
            inCompare
              ? "Убрать из сравнения"
              : isFull
                ? "Лимит сравнения — 3 продукта"
                : "Добавить в сравнение"
          }
          aria-pressed={inCompare}
        >
          {inCompare ? "✓ В сравнении" : "+ В сравнение"}
        </button>
      </div>

      <div className="product-card__body">
        <header className="product-card__header">
          <h3 className="product-card__title">{entry.name}</h3>
          <p className="product-card__tagline">{entry.tagline}</p>
        </header>

        <p className="product-card__description">{entry.description}</p>

        {entry.roi_metrics.length > 0 && (
          <ul className="product-card__roi">
            {entry.roi_metrics.slice(0, 3).map((m) => (
              <li key={m.metric} className="product-card__roi-item">
                <span className="product-card__roi-value" style={{ color: entry.accent_color }}>
                  {m.value}
                </span>
                <span className="product-card__roi-label">{m.metric}</span>
              </li>
            ))}
          </ul>
        )}

        <footer className="product-card__footer">
          <div className="product-card__icp">
            {entry.icp.map((c) => (
              <span key={c} className="product-card__icp-chip" title={`ICP персона ${c}`}>
                {c}
              </span>
            ))}
          </div>
          <div className="product-card__industry">
            {entry.industry.slice(0, 2).map((ind) => (
              <span key={ind} className="product-card__tag">
                {ind}
              </span>
            ))}
            {entry.industry.length > 2 && (
              <span className="product-card__tag product-card__tag--muted">
                +{entry.industry.length - 2}
              </span>
            )}
          </div>
        </footer>

        <div className="product-card__actions">
          <Link
            to={`/product/${entry.slug}`}
            className="btn-secondary product-card__details"
          >
            Подробнее
          </Link>
          {entry.demo_status === "live" ? (
            <Link
              to={`/demo/${entry.slug}`}
              className="btn-primary product-card__launch"
              style={{ backgroundColor: entry.accent_color }}
            >
              Запустить demo →
            </Link>
          ) : (
            <span
              className="product-card__launch product-card__launch--coming"
              title="Demo в разработке"
            >
              🚧 Demo скоро
            </span>
          )}
        </div>
      </div>
    </article>
  );
}
